import type { ASNFormData, Order, LineItemDetail, NameEntry } from '../types/asn';
import type { TemplateConfig } from '../types/tradingPartner';

/**
 * Build a seed NameEntry for an `addNamesByDefault` requirement item.
 * Uses the per-entity overrides on the item when present, falling back to
 * `templates.name.identificationCodeQualifier` for the qualifier. Unset
 * fields remain empty strings so the form renders inputs rather than
 * "undefined" placeholder text.
 */
function seedNameFromRequirement(
  req: NonNullable<NonNullable<TemplateConfig['prePopulateRequirements']>['orders']>['addNamesByDefault'] extends (infer U)[] | undefined ? U : never,
  templateConfig: TemplateConfig,
): NameEntry {
  const entry: NameEntry = {
    type: req.entityIdentifierCode,
    name: req.name ?? '',
    qualifier: req.qualifier ?? templateConfig.templates.name.identificationCodeQualifier ?? '',
    id: req.id ?? '',
  };
  if (req.address1 !== undefined)  entry.address1  = req.address1;
  if (req.address2 !== undefined)  entry.address2  = req.address2;
  if (req.city !== undefined)      entry.city      = req.city;
  if (req.state !== undefined)     entry.state     = req.state;
  if (req.postalCode !== undefined) entry.postalCode = req.postalCode;
  if (req.country !== undefined)   entry.country   = req.country;
  return entry;
}

export function applyTemplateToFormData(
  baseFormData: ASNFormData,
  templateConfig: TemplateConfig
): ASNFormData {
  const { defaults, prePopulateRequirements } = templateConfig;

  const updatedFormData: ASNFormData = {
    ...baseFormData,
    ...defaults.header,
  };

  if (prePopulateRequirements?.orders) {
    const minOrders = prePopulateRequirements.orders.min || 1;
    const orders: Order[] = [];

    for (let i = 0; i < minOrders; i++) {
      const order: Order = {
        orderNumber: '',
        orderDate: updatedFormData.date,
        names: [],
        details: [],
        ...defaults.orders,
      };

      if (prePopulateRequirements.orders.addNamesByDefault) {
        order.names = prePopulateRequirements.orders.addNamesByDefault.map((req) =>
          seedNameFromRequirement(req, templateConfig),
        );
      }

      if (prePopulateRequirements.lineItems) {
        const minLines = prePopulateRequirements.lineItems.min || 1;
        const details: LineItemDetail[] = [];

        for (let j = 0; j < minLines; j++) {
          const lineItem: LineItemDetail = {
            line_number: j + 1,
            unitsShipped: '',
            uom: defaults.lineItems?.unitOfMeasure || '',
            description: '',
            customerOrderNumber: '',
            orderAssignedId: String(j + 1),
            parts: [],
            references: [],
            loads: {
              numberOfLoads: 1,
              unitsShipped: 0,
              // A12 (Natalie): line-loads packaging default. Read from the
              // template (defaults.lineItems.packagingCode) so a carton default
              // can be configured; PLT71 stays the fallback for templates that
              // don't declare one.
              packagingCode: defaults.lineItems?.packagingCode || 'PLT71',
              serials: [],
            },
          };

          if (prePopulateRequirements.lineItems.addPartInfoByDefault) {
            if (Array.isArray(prePopulateRequirements.lineItems.addPartInfoByDefault)) {
              lineItem.parts = prePopulateRequirements.lineItems.addPartInfoByDefault.map((part) => ({
                qualifier: part.qualifier,
                id: part.id ?? '', // A11: pre-fill (e.g. CH → 'US'); else empty
              }));
            } else {
              lineItem.parts = [
                {
                  qualifier: templateConfig.templates.partInfo.commodityCodeQualifier,
                  id: '',
                },
              ];
            }
          }

          if (prePopulateRequirements.lineItems.addReferencesByDefault) {
            lineItem.references = prePopulateRequirements.lineItems.addReferencesByDefault.map((refTemplate) => ({
              qualifier: refTemplate.qualifier,
              id: '',
            }));
          }

          details.push(lineItem);
        }

        order.details = details;
      }

      orders.push(order);
    }

    updatedFormData.orders = orders;
  }

  return updatedFormData;
}

export function createEmptyName(templateConfig: TemplateConfig, entityCode?: string) {
  const template = templateConfig.templates.name;
  return {
    type: entityCode || template.entityIdentifierCode,
    name: '',
    qualifier: template.identificationCodeQualifier || '',
    id: '',
  };
}

export function createEmptyPart(templateConfig: TemplateConfig) {
  const template = templateConfig.templates.partInfo;
  return {
    qualifier: template.commodityCodeQualifier,
    id: '',
  };
}

export function createEmptyReference(templateConfig: TemplateConfig, qualifier?: string) {
  const template = templateConfig.templates.reference;
  return {
    qualifier: qualifier || template.qualifier,
    id: '',
  };
}

export function createEmptySerial(): string {
  return '';
}

export function createEmptyOrder(
  templateConfig: TemplateConfig | undefined,
  orderIndex: number,
  currentDate: string
): Order {
  const order: Order = {
    orderNumber: '',
    orderDate: currentDate,
    orderAssignedId: String(orderIndex + 1),
    names: [],
    details: [],
  };

  if (templateConfig?.defaults?.orders) {
    Object.assign(order, templateConfig.defaults.orders);
  }

  if (templateConfig?.prePopulateRequirements?.orders?.addNamesByDefault) {
    order.names = templateConfig.prePopulateRequirements.orders.addNamesByDefault.map((req) =>
      seedNameFromRequirement(req, templateConfig),
    );
  }

  return order;
}

export function createEmptyLineItem(
  templateConfig: TemplateConfig | undefined,
  lineNumber: number
): LineItemDetail {
  const lineItem: LineItemDetail = {
    line_number: lineNumber,
    unitsShipped: '',
    uom: templateConfig?.defaults?.lineItems?.unitOfMeasure || 'PC',
    description: '',
    customerOrderNumber: '',
    orderAssignedId: String(lineNumber),
    parts: [],
    references: [],
    loads: {
      numberOfLoads: 1,
      unitsShipped: 0,
      // Read the configured default (A12) the same way the initial-create path
      // does. Hardcoding PLT71 here made the carton default apply to line 1 and
      // silently revert on every line the operator added.
      packagingCode: templateConfig?.defaults?.lineItems?.packagingCode || 'PLT71',
      serials: [],
    },
  };

  if (templateConfig?.prePopulateRequirements?.lineItems?.addPartInfoByDefault) {
    if (Array.isArray(templateConfig.prePopulateRequirements.lineItems.addPartInfoByDefault)) {
      lineItem.parts = templateConfig.prePopulateRequirements.lineItems.addPartInfoByDefault.map((part) => ({
        qualifier: part.qualifier,
        // A11: carry the pre-fill (e.g. CH -> 'US'); dropping it here lost the
        // Country of Origin default on added lines.
        id: part.id ?? '',
      }));
    } else {
      lineItem.parts = [
        {
          qualifier: templateConfig.templates.partInfo.commodityCodeQualifier,
          id: '',
        },
      ];
    }
  }

  if (templateConfig?.prePopulateRequirements?.lineItems?.addReferencesByDefault) {
    lineItem.references = templateConfig.prePopulateRequirements.lineItems.addReferencesByDefault.map(
      (refTemplate) => ({
        qualifier: refTemplate.qualifier,
        id: '',
      })
    );
  }

  return lineItem;
}

export function isMandatoryField(
  templateConfig: TemplateConfig | undefined,
  section: 'header' | 'order' | 'lineItem' | 'names' | 'partInfo' | 'references' | 'serialNumbers',
  fieldName: string
): boolean {
  if (!templateConfig) return false;
  return templateConfig.mandatoryFields[section]?.includes(fieldName) || false;
}
